import i18n from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { MenuItem, useTheme } from '@material-ui/core';
import Select from '@material-ui/core/Select';
import useStyles from './MissionContactList.styles';
import ContactsForm from '../form/ContactsForm';
import { Table } from '../../../../../table/components';
import { Icon } from '../../../../../common/components/icons';
import { sizeEnum } from '../../../../../common/components/icons/Icon';
import Can from '../../../../../common/contexts/abilityContext';
import useAgent from '../../../../../common/hooks/useAgent';
import clientAgent from '../../../../../client/agents/clientAgent';
import { ContainerLoader } from '../../../../../common/components/loaders';

const MissionContactList = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [currentJurisdictionIndex, setCurrentJurisdictionIndex] = useState();

  const { values, errors, submitCount, setFieldValue } = useFormikContext();

  const [fetchClients, clients, isFetchingClients] = useAgent(clientAgent.fetchClients);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const { current: columns } = useRef([
    { Header: i18n.t('mission.table.name'), accessor: 'name', columnWidth: 10 },
    { Header: i18n.t('mission.table.phone'), accessor: 'phoneNumber', columnWidth: 10 },
    { Header: i18n.t('mission.table.email'), accessor: 'email', columnWidth: 10 },
  ]);

  const handleEditClick = useCallback(
    ({ zoneId }) => {
      const jurisdiction = values.jurisdictions.map((j) => j.zoneId);
      setCurrentJurisdictionIndex(jurisdiction.indexOf(zoneId));
    },
    [values.jurisdictions]
  );

  const rowActions = useMemo(() => [{ icon: 'edit', onClick: handleEditClick }], [handleEditClick]);

  const errorIcon = useMemo(() => <Icon icon="warning" size={sizeEnum.small} className={classes.errorIcon} />, [classes.errorIcon]);

  const getJurisdictionContacts = useCallback(
    (jurisdiction, jurisdictionIndex) => {
      const contacts = [];

      if (jurisdiction.contacts && jurisdiction.contacts.length > 0) {
        jurisdiction.contacts.forEach((contact, contactIndex) => {
          const error =
            submitCount > 0 &&
            errors &&
            errors.jurisdictions &&
            errors.jurisdictions[jurisdictionIndex] &&
            errors.jurisdictions[jurisdictionIndex].contacts &&
            errors.jurisdictions[jurisdictionIndex].contacts[contactIndex];

          contacts.push({
            zoneId: jurisdiction.zoneId,
            name: error && error.name ? errorIcon : contact.name,
            phoneNumber: error && error.phoneNumber ? errorIcon : contact.phoneNumber,
            email: error && error.email ? errorIcon : contact.email,
          });
        });
      } else {
        contacts.push({
          zoneId: jurisdiction.zoneId,
          name: '',
          phoneNumber: '',
          email: '',
        });

        setFieldValue(`jurisdictions.${jurisdictionIndex}.contacts`, contacts);
      }

      return contacts;
    },
    [submitCount, errors, errorIcon, setFieldValue]
  );

  const handleContactSubmit = useCallback(() => {
    setCurrentJurisdictionIndex(undefined);
  }, []);

  if (!values) return null;

  if (!clients || isFetchingClients) return <ContainerLoader />;

  return (
    <>
      {currentJurisdictionIndex >= 0 && <ContactsForm jurisdictionIndex={currentJurisdictionIndex} onSubmit={handleContactSubmit} />}

      <div className={classes.title}>{i18n.t('mission.contactsPerJurisdictions')}</div>
      <div className={classes.jurisdictions}>
        {values.jurisdictions.map((jurisdiction, index) => (
          <div className={classes.jurisdiction} key={jurisdiction.zoneId}>
            {!values.sharedRow && <div>{jurisdiction.zoneId}</div>}
            {values.sharedRow && (
              <Can do="create" on="client" passThrough>
                {(canChangeClient) => (
                  <Select
                    className={classes.clientSelector}
                    value={jurisdiction.clientId ? jurisdiction.clientId : ''}
                    defaultValue=""
                    disabled={!canChangeClient}
                    required
                    onChange={(event) => {
                      const selectedClientId = event.target.value;
                      const updatedJurisdictions = [...values.jurisdictions];
                      updatedJurisdictions[index].clientId = selectedClientId;
                      setFieldValue(values.jurisdictions, updatedJurisdictions);
                    }}>
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Can>
            )}
            <Table
              columns={columns}
              data={getJurisdictionContacts(jurisdiction, index)}
              rowActions={rowActions}
              headerStyle={{ backgroundColor: theme.palette.container.background }}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default MissionContactList;
